
import React, { useState } from 'react';
import { useAuth } from '@/hooks/useAuth';
import { useInternalAuth } from '@/hooks/useInternalAuth';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Switch } from '@/components/ui/switch';
import { toast } from 'sonner';
import { Loader2, Save, User, Camera, Heart } from 'lucide-react';

const FanProfileForm: React.FC = () => {
  const { user: supabaseUser, profile } = useAuth();
  const { user: internalUser } = useInternalAuth();
  const [loading, setLoading] = useState(false);
  const [photoPreview, setPhotoPreview] = useState<string | null>(null);
  
  const [formData, setFormData] = useState({
    // Basic Information
    first_name: profile?.first_name || '',
    last_name: profile?.last_name || '',
    username: profile?.username || internalUser?.username || '',
    phone: profile?.phone || '',
    
    // Fan specific fields
    favorite_teams: profile?.favorite_teams || '',
    favorite_players: profile?.favorite_players || '',
    fan_since: profile?.fan_since || '',
    memorable_games: profile?.memorable_games || '',
    fan_traditions: profile?.fan_traditions || '',
    
    // Sports preferences
    preferred_sports: profile?.preferred_sports || [],
    
    // Profile Photo
    profile_photo: null as File | null,
    profile_photo_url: profile?.profile_photo_url || '',
    
    // Privacy settings
    show_profile_public: profile?.show_profile_public || false,
    show_contact_public: profile?.show_contact_public || false,
  });

  const handleInputChange = (field: string, value: any) => {
    setFormData(prev => ({ ...prev, [field]: value }));
  };

  const handlePhotoChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (file) {
      setFormData(prev => ({ ...prev, profile_photo: file }));
      
      const reader = new FileReader();
      reader.onload = (e) => {
        setPhotoPreview(e.target?.result as string);
      };
      reader.readAsDataURL(file);
    }
  };

  const handleArrayChange = (field: string, value: string, checked: boolean) => {
    setFormData(prev => ({
      ...prev,
      [field]: checked 
        ? [...prev[field], value]
        : prev[field].filter((item: string) => item !== value)
    }));
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setLoading(true);

    try {
      toast.success('Fan profile updated successfully!');
    } catch (error) {
      console.error('Error updating profile:', error);
      toast.error('Failed to update profile');
    } finally {
      setLoading(false);
    }
  };

  const sportsOptions = [
    'Football', 'Basketball', 'Soccer', 'Baseball', 'Tennis', 
    'Golf', 'Swimming', 'Track & Field', 'Volleyball', 'Hockey'
  ];

  return (
    <div className="space-y-6">
      <form onSubmit={handleSubmit} className="space-y-6">
        {/* Profile Photo Section */}
        <Card className="bg-gray-800 border-gray-700">
          <CardHeader>
            <CardTitle className="text-white flex items-center gap-2">
              <Camera className="h-5 w-5" />
              Profile Photo
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="flex items-center gap-6">
              <div className="w-24 h-24 bg-gray-700 rounded-full flex items-center justify-center overflow-hidden">
                {photoPreview || formData.profile_photo_url ? (
                  <img 
                    src={photoPreview || formData.profile_photo_url} 
                    alt="Profile" 
                    className="w-full h-full object-cover"
                  />
                ) : (
                  <User className="h-12 w-12 text-gray-400" />
                )}
              </div>
              <div>
                <Label htmlFor="profile_photo" className="cursor-pointer">
                  <div className="bg-sports-green text-black px-4 py-2 rounded-lg hover:bg-green-600 transition-colors inline-flex items-center gap-2">
                    <Camera className="h-4 w-4" />
                    Upload Photo
                  </div>
                </Label>
                <Input
                  id="profile_photo"
                  type="file"
                  accept="image/*"
                  onChange={handlePhotoChange}
                  className="hidden"
                />
              </div>
            </div>
          </CardContent>
        </Card>

        {/* Basic Information */}
        <Card className="bg-gray-800 border-gray-700">
          <CardHeader>
            <CardTitle className="text-white">Basic Information</CardTitle>
          </CardHeader>
          <CardContent className="space-y-4">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <Label htmlFor="first_name" className="text-gray-300">First Name *</Label>
                <Input
                  id="first_name"
                  value={formData.first_name}
                  onChange={(e) => handleInputChange('first_name', e.target.value)}
                  className="bg-gray-700 border-gray-600 text-white"
                  required
                />
              </div>
              <div>
                <Label htmlFor="last_name" className="text-gray-300">Last Name *</Label>
                <Input
                  id="last_name"
                  value={formData.last_name}
                  onChange={(e) => handleInputChange('last_name', e.target.value)}
                  className="bg-gray-700 border-gray-600 text-white"
                  required
                />
              </div>
              <div>
                <Label htmlFor="username" className="text-gray-300">Username *</Label>
                <Input
                  id="username"
                  value={formData.username}
                  onChange={(e) => handleInputChange('username', e.target.value)}
                  className="bg-gray-700 border-gray-600 text-white"
                  required
                />
              </div>
              <div>
                <Label htmlFor="phone" className="text-gray-300">Phone Number</Label>
                <Input
                  id="phone"
                  type="tel"
                  value={formData.phone}
                  onChange={(e) => handleInputChange('phone', e.target.value)}
                  className="bg-gray-700 border-gray-600 text-white"
                />
              </div>
            </div>
          </CardContent>
        </Card>

        {/* Fan Information */}
        <Card className="bg-gray-800 border-gray-700">
          <CardHeader>
            <CardTitle className="text-white flex items-center gap-2">
              <Heart className="h-5 w-5" />
              Fan Information
            </CardTitle>
          </CardHeader>
          <CardContent className="space-y-4">
            <div>
              <Label htmlFor="favorite_teams" className="text-gray-300">Favorite Teams</Label>
              <Textarea
                id="favorite_teams"
                value={formData.favorite_teams}
                onChange={(e) => handleInputChange('favorite_teams', e.target.value)}
                placeholder="List your favorite teams..."
                className="bg-gray-700 border-gray-600 text-white"
              />
            </div>
            
            <div>
              <Label htmlFor="favorite_players" className="text-gray-300">Favorite Players</Label>
              <Textarea
                id="favorite_players"
                value={formData.favorite_players}
                onChange={(e) => handleInputChange('favorite_players', e.target.value)}
                placeholder="Who are your favorite players?"
                className="bg-gray-700 border-gray-600 text-white"
              />
            </div>
            
            <div>
              <Label htmlFor="fan_since" className="text-gray-300">Fan Since</Label>
              <Input
                id="fan_since"
                value={formData.fan_since}
                onChange={(e) => handleInputChange('fan_since', e.target.value)}
                placeholder="e.g., 2010, Childhood, etc."
                className="bg-gray-700 border-gray-600 text-white"
              />
            </div>
            
            <div>
              <Label htmlFor="memorable_games" className="text-gray-300">Most Memorable Games</Label>
              <Textarea
                id="memorable_games"
                value={formData.memorable_games}
                onChange={(e) => handleInputChange('memorable_games', e.target.value)}
                placeholder="Describe games that you'll never forget..."
                className="bg-gray-700 border-gray-600 text-white"
              />
            </div>
            
            <div>
              <Label htmlFor="fan_traditions" className="text-gray-300">Fan Traditions</Label>
              <Textarea
                id="fan_traditions"
                value={formData.fan_traditions}
                onChange={(e) => handleInputChange('fan_traditions', e.target.value)}
                placeholder="What traditions do you have as a fan?"
                className="bg-gray-700 border-gray-600 text-white"
              />
            </div>
          </CardContent>
        </Card>

        {/* Sports Preferences */}
        <Card className="bg-gray-800 border-gray-700">
          <CardHeader>
            <CardTitle className="text-white">Sports Preferences</CardTitle>
          </CardHeader>
          <CardContent>
            <div className="grid grid-cols-2 md:grid-cols-3 gap-3">
              {sportsOptions.map((sport) => (
                <div key={sport} className="flex items-center space-x-2">
                  <input
                    type="checkbox"
                    id={sport}
                    checked={formData.preferred_sports.includes(sport)}
                    onChange={(e) => handleArrayChange('preferred_sports', sport, e.target.checked)}
                    className="w-4 h-4 text-sports-green bg-gray-700 border-gray-600 rounded focus:ring-sports-green"
                  />
                  <Label htmlFor={sport} className="text-gray-300 text-sm cursor-pointer">
                    {sport}
                  </Label>
                </div>
              ))}
            </div>
          </CardContent>
        </Card>

        {/* Privacy Settings */}
        <Card className="bg-gray-800 border-gray-700">
          <CardHeader>
            <CardTitle className="text-white">Privacy Settings</CardTitle>
          </CardHeader>
          <CardContent className="space-y-4">
            <div className="flex items-center justify-between p-4 bg-gray-700 rounded-lg">
              <div>
                <Label className="text-white font-medium">Make Profile Public</Label>
                <p className="text-sm text-gray-400">Allow others to view your profile</p>
              </div>
              <Switch
                checked={formData.show_profile_public}
                onCheckedChange={(checked) => handleInputChange('show_profile_public', checked)}
              />
            </div>
            
            <div className="flex items-center justify-between p-4 bg-gray-700 rounded-lg">
              <div>
                <Label className="text-white font-medium">Show Contact Information</Label>
                <p className="text-sm text-gray-400">Display contact info publicly</p>
              </div>
              <Switch
                checked={formData.show_contact_public}
                onCheckedChange={(checked) => handleInputChange('show_contact_public', checked)}
              />
            </div>
          </CardContent>
        </Card>

        {/* Submit Button */}
        <div className="flex justify-end">
          <Button
            type="submit"
            disabled={loading}
            className="bg-sports-green text-black hover:bg-green-600"
          >
            {loading ? (
              <>
                <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                Saving...
              </>
            ) : (
              <>
                <Save className="mr-2 h-4 w-4" />
                Save Fan Profile
              </>
            )}
          </Button>
        </div>
      </form>
    </div>
  );
};

export default FanProfileForm;
